import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, filter, map, of, tap } from 'rxjs';
import { EloActions, HomiSettingsActions } from './homi-settings.actions';
import { ApplicationFacade } from '@lyceo/shared/data-access';
import { HomiSettingsService } from '../../homi-settings.service';
import { HomiSettingsFacade } from './homi-settings.facade';
import { isDefined } from '@lyceo/shared/util';
import { openUri } from '@lyceo/shared/util';
import { routerNavigatedAction } from '@ngrx/router-store';
import { Router } from '@angular/router';

@Injectable()
export class HomiSettingsEffects {
  private readonly actions$ = inject(Actions);
  private readonly homiSettingsService = inject(HomiSettingsService);
  private readonly homiSettingsFacade: HomiSettingsFacade =
    inject(HomiSettingsFacade);
  private readonly applicationFacade: ApplicationFacade =
    inject(ApplicationFacade);
  private readonly router: Router = inject(Router);

  public refreshHomiSettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HomiSettingsActions.refreshSettings),
      exhaustMap(() =>
        this.homiSettingsService.getHomiSettings().pipe(
          map((settings) => {
            return HomiSettingsActions.refreshSettingsSuccess({
              settings,
            });
          }),
          catchError((error) =>
            of(HomiSettingsActions.refreshSettingsFailure({ error })),
          ),
        ),
      ),
    );
  });

  public getHomiSettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HomiSettingsActions.getSettings),
      exhaustMap(() =>
        this.homiSettingsService.getHomiSettings().pipe(
          map((settings) => {
            return HomiSettingsActions.getSettingsSuccess({
              settings,
            });
          }),
          catchError((error) =>
            of(HomiSettingsActions.getSettingsFailure({ error })),
          ),
        ),
      ),
    );
  });

  public getHomiSettingsByPupilId$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HomiSettingsActions.getSettingsByPupilId),
      exhaustMap((action) =>
        this.homiSettingsService.getHomiSettingsByPupilId(action.pupilId).pipe(
          map((settings) => {
            return HomiSettingsActions.getSettingsSuccess({
              settings,
            });
          }),
          catchError((error) =>
            of(HomiSettingsActions.getSettingsFailure({ error })),
          ),
        ),
      ),
    );
  });

  public refreshHomiSettingsMobile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HomiSettingsActions.connectEloSuccess),
      map(() => HomiSettingsActions.refreshSettings()),
    );
  });

  public syncEloClicked$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EloActions.syncEloClicked, HomiSettingsActions.connectEloSuccess),
      map(() => this.homiSettingsFacade.pupilId()),
      filter(isDefined),
      map((pupilId) => HomiSettingsActions.syncElo({ pupilId })),
    );
  });

  public syncElo$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HomiSettingsActions.syncElo),
      exhaustMap((action) =>
        this.homiSettingsService.syncElo(action.pupilId).pipe(
          map((results) => {
            return HomiSettingsActions.syncEloSuccess({ results });
          }),
          catchError((error) =>
            of(HomiSettingsActions.syncEloFailure({ error })),
          ),
        ),
      ),
    );
  });

  public syncEloSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(HomiSettingsActions.syncEloSuccess),
        tap(({ results }) => {
          const elo: string =
            this.homiSettingsFacade.eloDisplayValue() ?? 'Elo';
          this.applicationFacade.showToaster({
            type: 'success',
            message: `${elo} synchronisatie voltooid. Er zijn ${results.tasksAdded} taken toegevoegd en ${results.tasksRemoved} verwijderd.`,
          });
        }),
      );
    },
    { dispatch: false },
  );

  public syncEloFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(HomiSettingsActions.syncEloFailure),
        tap(() => {
          const elo: string =
            this.homiSettingsFacade.eloDisplayValue() ?? 'de elo';
          this.applicationFacade.showToaster({
            type: 'error',
            message: `Er ging iets fout bij het synchroniseren van ${elo}.`,
          });
        }),
      );
    },
    { dispatch: false },
  );

  public connectElo$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(EloActions.connectEloClicked),
        map(() => this.homiSettingsFacade.eloSettingsUri()),
        filter(isDefined),
        tap((uri) => {
          openUri(uri);
        }),
      );
    },
    { dispatch: false },
  );

  public connectEloResult$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(routerNavigatedAction),
      filter(
        (action) =>
          !!action.payload.routerState.url.match(/\/settings\?elo_success/),
      ),
      map((action) => {
        if (action.payload.routerState.url === '/settings?elo_success=true') {
          return HomiSettingsActions.connectEloSuccess();
        }

        return HomiSettingsActions.connectEloFailure();
      }),
    );
  });

  public connectEloResultNavigation$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          HomiSettingsActions.connectEloSuccess,
          HomiSettingsActions.connectEloFailure,
        ),
        map(() => {
          this.router.navigate(['/settings']);
        }),
      );
    },
    { dispatch: false },
  );

  public connectEloSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(HomiSettingsActions.connectEloSuccess),
        tap(() => {
          const elo: string =
            this.homiSettingsFacade.eloDisplayValue() ?? 'Elo';
          this.applicationFacade.showToaster({
            type: 'success',
            message: `${elo} koppeling geslaagd!`,
          });
        }),
      );
    },
    { dispatch: false },
  );

  public connectEloFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(HomiSettingsActions.connectEloFailure),
        tap(() => {
          const elo: string =
            this.homiSettingsFacade.eloDisplayValue() ?? 'de elo';
          this.applicationFacade.showToaster({
            type: 'error',
            message: `Het is niet gelukt om een koppeling met ${elo} te maken.`,
          });
        }),
      );
    },
    { dispatch: false },
  );
}
